import type { CrowdexReferenceKind } from '@crowdex/agent-contracts';
import { ConversationPaneSlot, removeLayoutPane } from '@/components/conversation-panel-layout';
import { SplitConversationPane } from '@/components/split-conversation-pane';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { resolveAgentForRuntime, resolvePersistedAgent } from '@/lib/desktop-shell/cloud-agents';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import type { NativeAgent } from '@/lib/native-desktop';

/**
 * The secondary conversation panes docked alongside the primary one. Each split
 * pane is backed by a cached conversation and manages its own draft; all of that
 * state lives in the store, so only the shared agent/runtime arrive as props. Rendered inside `ConversationPanelRoot` as sibling slots.
 */
export function SplitConversationPanes({
  agents,
  onActivate,
  preview,
  resolveReference,
}: {
  agents: readonly NativeAgent[];
  /** Makes a pane's conversation the active one, whose side panel shows. */
  onActivate: (threadId: string) => void;
  preview: boolean;
  resolveReference: (kind: CrowdexReferenceKind, id: string) => Promise<unknown>;
}) {
  const chatId = useDesktopShellStore((state) => state.chatId);
  const splitThreadIds = useDesktopShellStore((state) => state.splitThreadIds);
  const setSplitThreadIds = useDesktopShellStore((state) => state.setSplitThreadIds);
  const conversationCache = useDesktopShellStore((state) => state.conversationCache);
  const setConversationCache = useDesktopShellStore((state) => state.setConversationCache);
  const setConversationLayout = useDesktopShellStore((state) => state.setConversationLayout);
  const paneDrafts = useDesktopShellStore((state) => state.paneDrafts);
  const setPaneDrafts = useDesktopShellStore((state) => state.setPaneDrafts);

  return (
    <>
      {splitThreadIds.map((threadId) => {
        const conversation = conversationCache[threadId];
        if (conversation === undefined) return null;
        const runtime = conversation.runtime ?? 'local';
        const storedAgent = resolvePersistedAgent(agents, conversation.agentId);
        const paneAgent =
          resolveAgentForRuntime(
            agents,
            storedAgent?.command,
            runtime === 'cloud' ? 'cloud' : 'local',
          ) ??
          storedAgent ??
          null;
        const paneCloudRuntime: CloudRuntime | null =
          runtime === 'cloud' && paneAgent !== null
            ? {
                id: conversation.id,
                providerId: 'crowdex-cloud',
                profileId: 'standard',
                name: conversation.title,
                url: 'wss://server-managed.invalid',
                token: 'server-managed',
                workspaceRoot: '/workspace',
                workspaceIdentity: conversation.workspaceRoot ?? conversation.id,
                agentId: paneAgent.id,
                created: false,
                resumed: true,
              }
            : null;
        return (
          <ConversationPaneSlot key={threadId} paneId={threadId}>
            <SplitConversationPane
              agent={paneAgent}
              cloudRuntime={paneCloudRuntime}
              conversation={conversation}
              draft={paneDrafts[threadId] ?? ''}
              onClose={() => {
                setSplitThreadIds((current) => current.filter((id) => id !== threadId));
                setConversationLayout(
                  (current) => removeLayoutPane(current, threadId) ?? { kind: 'pane', id: chatId },
                );
              }}
              onDraftChange={(value) =>
                setPaneDrafts((current) => ({ ...current, [threadId]: value }))
              }
              onActivate={() => onActivate(threadId)}
              onConversationChange={(nextConversation) =>
                setConversationCache((current) => {
                  const previous = current[threadId];
                  if (
                    previous?.messages === nextConversation.messages &&
                    previous.collaboration === nextConversation.collaboration &&
                    previous.title === nextConversation.title &&
                    previous.messageRevision === nextConversation.messageRevision &&
                    previous.restoredMessageCount === nextConversation.restoredMessageCount
                  ) {
                    return current;
                  }
                  return { ...current, [threadId]: nextConversation };
                })
              }
              preview={preview}
              resolveReference={resolveReference}
            />
          </ConversationPaneSlot>
        );
      })}
    </>
  );
}
